import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, Download, Link2, Pencil, Plus, Search, Trash2, Upload } from 'lucide-react';
import { useUIColors } from '../../../../hooks/useTheme';

const money = (value) =>
  new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const Stacked = ({ top, bottom, topColor, bottomColor, muted }) => (
  <div style={{ lineHeight: 1.25 }}>
    <div style={{ fontWeight: 700, fontSize: 12, color: topColor }}>{top || '—'}</div>
    {bottom ? (
      <div style={{ fontSize: 11, color: bottomColor || muted, marginTop: 1 }}>{bottom}</div>
    ) : null}
  </div>
);

const COLLECTION_STATUS_ORDER = ['Pagada', 'Por vencer', 'Vencida', 'Anulada', 'Anulado', 'No aplica'];

const rowDisplayStatus = (row, useCollection = false) => {
  if (useCollection && String(row?.collection_status || '').trim()) {
    return String(row.collection_status).trim();
  }
  return String(row?.status || '').trim();
};

const formatIsoDateEs = (value) => {
  const raw = String(value || '').trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return '';
  const [year, month, day] = raw.split('-');
  return `${day}-${month}-${year}`;
};

const collectionDueHint = (row) => {
  const status = String(row?.collection_status || '').trim();
  const due = formatIsoDateEs(row?.due_date);
  const paid = formatIsoDateEs(row?.paid_date);
  const days = Number(row?.days_until_due);
  const daysLabel = Number.isFinite(days) ? `${Math.abs(days)} d` : '';
  if (status === 'Pagada') return paid ? `Pagada ${paid}` : due ? `Vencía ${due}` : '';
  if (status === 'Vencida' && due) return daysLabel ? `Venció ${due} · ${daysLabel}` : `Venció ${due}`;
  if (status === 'Por vencer' && due) return daysLabel ? `Vence ${due} · ${daysLabel}` : `Vence ${due}`;
  return '';
};

const statusPresentation = (statusRaw, isDarkMode) => {
  const s = String(statusRaw || '')
    .trim()
    .toLowerCase();
  const label = statusRaw && String(statusRaw).trim() ? String(statusRaw).trim() : '—';
  if (s === 'pagada' || s === 'contabilizado') {
    return {
      label,
      bg: isDarkMode ? '#14532d' : '#dcfce7',
      color: isDarkMode ? '#bbf7d0' : '#166534',
      border: isDarkMode ? '#22c55e' : '#86efac',
    };
  }
  if (s === 'por vencer') {
    return {
      label,
      bg: isDarkMode ? '#78350f' : '#fef3c7',
      color: isDarkMode ? '#fde68a' : '#92400e',
      border: isDarkMode ? '#f59e0b' : '#fcd34d',
    };
  }
  if (s === 'vencida') {
    return {
      label,
      bg: isDarkMode ? '#7f1d1d' : '#fee2e2',
      color: isDarkMode ? '#fecaca' : '#991b1b',
      border: isDarkMode ? '#ef4444' : '#fca5a5',
    };
  }
  if (s === 'anulada') {
    return {
      label,
      bg: isDarkMode ? '#4c1d95' : '#ede9fe',
      color: isDarkMode ? '#ddd6fe' : '#5b21b6',
      border: isDarkMode ? '#8b5cf6' : '#c4b5fd',
    };
  }
  if (s === 'anulado' || s === 'no aplica') {
    return {
      label,
      bg: isDarkMode ? '#3f3f46' : '#f4f4f5',
      color: isDarkMode ? '#d4d4d8' : '#52525b',
      border: isDarkMode ? '#52525b' : '#d4d4d8',
    };
  }
  return {
    label,
    bg: isDarkMode ? '#1e3a5f' : '#e0f2fe',
    color: isDarkMode ? '#7dd3fc' : '#0369a1',
    border: isDarkMode ? '#0284c7' : '#7dd3fc',
  };
};

const buildTableColumns = (showSaleLinkColumn = false) => {
  const columns = [
    { key: 'Documento', align: 'left', sortKey: 'doc_date' },
    { key: 'Tipo', align: 'left', sortKey: 'document_type' },
    { key: 'Contraparte', align: 'left', sortKey: 'counterpart' },
  ];
  if (showSaleLinkColumn) {
    columns.push({ key: 'Venta comercial', align: 'left', sortKey: 'sale_code' });
  }
  columns.push(
    { key: 'Neto', align: 'right', sortKey: 'net_amount' },
    { key: 'IVA', align: 'right', sortKey: 'tax_amount' },
    { key: 'Total', align: 'right', sortKey: 'total_amount' },
    { key: 'Estado', align: 'center', sortKey: 'status' },
    { key: '', align: 'right', sortKey: null }
  );
  return columns;
};

const saleLinkMethodLabel = (method = '') => {
  const key = String(method || '').trim().toLowerCase();
  if (key === 'manual') return 'Vínculo manual';
  if (key === 'suggested') return 'Vínculo sugerido';
  if (key === 'imported') return 'Vínculo importado';
  return '';
};

const getSortValue = (row, sortKey) => {
  switch (sortKey) {
    case 'doc_date':
      return String(row.doc_date || '');
    case 'document_type':
      return String(row.document_type || '').toLowerCase();
    case 'counterpart':
      return `${row.counterpart_name || ''} ${row.counterpart_rut || ''}`.toLowerCase();
    case 'sale_code':
      return String(row.sale_code || '').trim().toLowerCase();
    case 'net_amount':
      return Number(row.net_amount) || 0;
    case 'tax_amount':
      return Number(row.tax_amount) || 0;
    case 'total_amount':
      return Number(row.total_amount) || 0;
    case 'status':
      return rowDisplayStatus(row, Boolean(row.collection_status)).toLowerCase();
    default:
      return '';
  }
};

const compareRows = (a, b, sortKey, sortDir) => {
  const mult = sortDir === 'asc' ? 1 : -1;
  const va = getSortValue(a, sortKey);
  const vb = getSortValue(b, sortKey);
  if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * mult;
  return String(va).localeCompare(String(vb), 'es', { numeric: true, sensitivity: 'base' }) * mult;
};

const shortDocTypeLabel = (type = '') => {
  const raw = String(type || '').trim();
  if (!raw) return 'Sin tipo';
  const key = raw
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '');
  if (key.includes('nota de credito')) return 'N. Crédito';
  if (key.includes('nota de debito')) return 'N. Débito';
  if (key.includes('boleta') && (key.includes('exent') || key.includes('no afect'))) {
    return 'Boleta exenta';
  }
  if (key.includes('boleta')) return 'Boleta';
  if (key.includes('guia')) return 'Guía despacho';
  if (key.includes('factura') && (key.includes('exent') || key.includes('no afect'))) {
    return 'Factura exenta';
  }
  if (key.includes('factura') && key.includes('exportacion')) return 'Factura exp.';
  if (key.includes('factura electronica')) return 'Factura elect.';
  if (key.includes('factura')) return 'Factura';
  if (raw.length <= 22) return raw;
  return `${raw.slice(0, 20)}…`;
};

/**
 * Libro de registros (ventas/compras/…): mismo look que Table3 / UsersTable3.
 */
const AccountingRegisterTable3 = ({
  rows = [],
  loading = false,
  error = '',
  isDarkMode = false,
  tableTitle = 'Libro',
  subtitleOverride,
  addButtonLabel = 'Nuevo registro',
  onAdd,
  onEdit,
  onDelete,
  onImport,
  importLabel = 'Importar consolidado SII',
  importing = false,
  onExport,
  exportLabel = 'Exportar',
  exporting = false,
  showDocTypeTabs = false,
  showSaleLinkColumn = false,
  onLinkSuggestedSale,
  linkingSuggestedSale = false,
  onReconcile,
  reconcileCount = 0,
  onRowClick,
}) => {
  const ui = useUIColors();
  const tableColumns = useMemo(
    () => buildTableColumns(showSaleLinkColumn),
    [showSaleLinkColumn]
  );
  const bg = ui.page;
  const headerBg = ui.header;
  const border = ui.border;
  const text = ui.text;
  const muted = ui.muted;
  const rowHover = ui.rowHover;
  const inputBg = ui.inputBg || ui.panel || bg;
  const inputBorder = ui.inputBorder || border;
  const padV = 9;
  const padH = 10;
  const thPadV = 8;

  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [docTypeTab, setDocTypeTab] = useState('all');
  const [sortColumn, setSortColumn] = useState('doc_date');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedIds, setSelectedIds] = useState([]);
  const headerSelectRef = useRef(null);

  const selectStyle = {
    height: 36,
    padding: '0 10px',
    borderRadius: 8,
    border: `1px solid ${inputBorder}`,
    background: inputBg,
    color: text,
    fontSize: 13,
    outline: 'none',
    boxSizing: 'border-box',
  };

  const statusOptions = useMemo(() => {
    const set = new Set();
    rows.forEach((row) => {
      const st = rowDisplayStatus(row, showSaleLinkColumn);
      if (st) set.add(st);
    });
    const list = Array.from(set);
    return list.sort((a, b) => {
      const ia = COLLECTION_STATUS_ORDER.indexOf(a);
      const ib = COLLECTION_STATUS_ORDER.indexOf(b);
      if (ia >= 0 || ib >= 0) return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
      return a.localeCompare(b, 'es');
    });
  }, [rows, showSaleLinkColumn]);

  const typeOptions = useMemo(() => {
    const set = new Set();
    rows.forEach((row) => {
      const t = String(row.document_type || '').trim();
      if (t) set.add(t);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'es'));
  }, [rows]);

  const typeCounts = useMemo(() => {
    const counts = { all: rows.length };
    rows.forEach((row) => {
      const t = String(row.document_type || '').trim() || 'Sin tipo';
      counts[t] = (counts[t] || 0) + 1;
    });
    return counts;
  }, [rows]);

  const docTypeTabs = useMemo(() => {
    const tabs = [{ key: 'all', label: 'Todos', fullLabel: 'Todos', count: typeCounts.all || 0 }];
    typeOptions.forEach((type) => {
      tabs.push({
        key: type,
        label: shortDocTypeLabel(type),
        fullLabel: type,
        count: typeCounts[type] || 0,
      });
    });
    const untyped = typeCounts['Sin tipo'] || 0;
    if (untyped > 0) {
      tabs.push({
        key: '__none__',
        label: 'Sin tipo',
        fullLabel: 'Sin tipo',
        count: untyped,
      });
    }
    return tabs;
  }, [typeOptions, typeCounts]);

  useEffect(() => {
    if (!showDocTypeTabs) return;
    if (docTypeTab === 'all' || docTypeTab === '__none__') return;
    if (!typeOptions.includes(docTypeTab)) setDocTypeTab('all');
  }, [showDocTypeTabs, docTypeTab, typeOptions]);

  const activeTypeFilter = showDocTypeTabs
    ? docTypeTab === 'all'
      ? ''
      : docTypeTab
    : filterType;

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return rows.filter((row) => {
      if (filterStatus && rowDisplayStatus(row, showSaleLinkColumn) !== filterStatus) return false;
      if (activeTypeFilter === '__none__') {
        if (String(row.document_type || '').trim()) return false;
      } else if (activeTypeFilter && String(row.document_type || '') !== activeTypeFilter) {
        return false;
      }
      if (!q) return true;
      const haystack = [
        row.doc_code,
        row.doc_number,
        row.doc_date,
        row.document_type,
        row.counterpart_name,
        row.counterpart_rut,
        row.description,
        row.status,
        row.collection_status,
        row.due_date,
        row.payment_terms,
        row.sale_code,
        row.link_method,
        row.sale_id,
        row.suggested_sale_code,
        row.has_pending_sale_match ? 'match venta comercial' : '',
        row.sale_code ? '' : 'sin vincular sin venta',
        row.notes,
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [rows, searchQuery, filterStatus, activeTypeFilter, showSaleLinkColumn]);

  const sortedRows = useMemo(() => {
    const copy = [...filteredRows];
    copy.sort((a, b) => compareRows(a, b, sortColumn, sortDir));
    return copy;
  }, [filteredRows, sortColumn, sortDir]);

  const totals = useMemo(
    () =>
      filteredRows.reduce(
        (acc, row) => {
          acc.net += Number(row.net_amount || 0);
          acc.tax += Number(row.tax_amount || 0);
          acc.total += Number(row.total_amount || 0);
          return acc;
        },
        { net: 0, tax: 0, total: 0 }
      ),
    [filteredRows]
  );

  const visibleIds = sortedRows.map((r) => r.id);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
  const someVisibleSelected = visibleIds.some((id) => selectedIds.includes(id));
  const showBulkChrome = typeof onDelete === 'function';

  useEffect(() => {
    if (headerSelectRef.current) {
      headerSelectRef.current.indeterminate = someVisibleSelected && !allVisibleSelected;
    }
  }, [someVisibleSelected, allVisibleSelected]);

  const handleSortClick = (sortKey) => {
    if (!sortKey) return;
    if (sortColumn === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortColumn(sortKey);
      setSortDir(sortKey.includes('amount') || sortKey === 'doc_date' ? 'desc' : 'asc');
    }
  };

  const SortIcon = ({ columnKey }) => {
    if (!columnKey) return null;
    if (sortColumn !== columnKey) return <ArrowUpDown size={12} color={muted} />;
    return sortDir === 'asc' ? <ArrowUp size={12} color={text} /> : <ArrowDown size={12} color={text} />;
  };

  const clearFilters = () => {
    setSearchQuery('');
    setFilterStatus('');
    setFilterType('');
  };

  const hasActiveFilters =
    Boolean(searchQuery.trim()) ||
    Boolean(filterStatus) ||
    (!showDocTypeTabs && Boolean(filterType));

  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
    }
  };

  const toggleRow = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleBulkDelete = async () => {
    if (!onDelete || selectedIds.length === 0) return;
    if (!window.confirm(`¿Anular ${selectedIds.length} registro(s)?`)) return;
    const selectedRows = rows.filter((r) => selectedIds.includes(r.id));
    for (const row of selectedRows) {
      // eslint-disable-next-line no-await-in-loop
      await onDelete(row);
    }
    setSelectedIds([]);
  };

  const subtitle =
    subtitleOverride ||
    (loading
      ? 'Cargando datos...'
      : error ||
        `${filteredRows.length} registro${filteredRows.length === 1 ? '' : 's'} · Neto ${money(
          totals.net
        )} · Total ${money(totals.total)}`);

  const colCount = tableColumns.length + (showBulkChrome ? 1 : 0);

  return (
    <div
      style={{
        border: `1px solid ${border}`,
        borderRadius: 12,
        overflow: 'hidden',
        background: bg,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 16px',
          borderBottom: `1px solid ${border}`,
          background: headerBg,
          flexWrap: 'wrap',
          gap: 10,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', minWidth: 0 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: text }}>{tableTitle}</h3>
          <p style={{ margin: 0, fontSize: 12, color: muted }}>{subtitle}</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {onImport ? (
            <button
              type="button"
              onClick={onImport}
              disabled={importing || exporting}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                height: 36,
                padding: '0 14px',
                borderRadius: 8,
                border: `1px solid ${inputBorder}`,
                background: inputBg,
                color: text,
                fontWeight: 600,
                fontSize: 13,
                cursor: importing ? 'wait' : 'pointer',
                opacity: importing ? 0.7 : 1,
              }}
            >
              <Upload size={16} /> {importing ? 'Importando...' : importLabel}
            </button>
          ) : null}
          {onExport ? (
            <button
              type="button"
              onClick={onExport}
              disabled={importing || exporting}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                height: 36,
                padding: '0 14px',
                borderRadius: 8,
                border: `1px solid ${inputBorder}`,
                background: inputBg,
                color: text,
                fontWeight: 600,
                fontSize: 13,
                cursor: exporting ? 'wait' : 'pointer',
                opacity: exporting ? 0.7 : 1,
              }}
            >
              <Download size={16} /> {exporting ? 'Exportando...' : exportLabel}
            </button>
          ) : null}
          {onReconcile ? (
            <button
              type="button"
              onClick={onReconcile}
              disabled={importing || exporting}
              title={
                reconcileCount
                  ? `${reconcileCount} factura(s) con venta sugerida por vincular`
                  : 'Sin facturas con venta sugerida pendiente'
              }
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                height: 36,
                padding: '0 14px',
                borderRadius: 8,
                border: `1px solid ${inputBorder}`,
                background: inputBg,
                color: text,
                fontWeight: 600,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              <Link2 size={16} /> Conciliar
              {reconcileCount ? (
                <span
                  style={{
                    minWidth: 20,
                    height: 20,
                    padding: '0 6px',
                    borderRadius: 999,
                    background: '#d97706',
                    color: '#fff',
                    fontSize: 11,
                    fontWeight: 700,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxSizing: 'border-box',
                  }}
                >
                  {reconcileCount}
                </span>
              ) : null}
            </button>
          ) : null}
          {onAdd ? (
            <button
              type="button"
              onClick={onAdd}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                height: 36,
                padding: '0 14px',
                borderRadius: 8,
                border: 'none',
                background: '#16a34a',
                color: '#fff',
                fontWeight: 600,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              <Plus size={16} /> {addButtonLabel}
            </button>
          ) : null}
        </div>
      </div>

      {showDocTypeTabs ? (
        <div
          role="tablist"
          aria-label="Tipo de documento"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 4,
            padding: '0 12px',
            borderBottom: `1px solid ${border}`,
            background: headerBg,
            overflowX: 'auto',
          }}
        >
          {docTypeTabs.map((tab) => {
            const active = docTypeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={active}
                title={tab.fullLabel}
                onClick={() => setDocTypeTab(tab.key)}
                style={{
                  appearance: 'none',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  padding: '10px 12px',
                  marginBottom: -1,
                  fontSize: 13,
                  fontWeight: active ? 700 : 500,
                  color: active ? text : muted,
                  borderBottom: active ? '2px solid #16a34a' : '2px solid transparent',
                  whiteSpace: 'nowrap',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <span>{tab.label}</span>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: active ? (isDarkMode ? '#86efac' : '#15803d') : muted,
                    background: active
                      ? isDarkMode
                        ? 'rgba(22, 163, 74, 0.2)'
                        : 'rgba(22, 163, 74, 0.12)'
                      : isDarkMode
                        ? 'rgba(255,255,255,0.06)'
                        : 'rgba(0,0,0,0.05)',
                    borderRadius: 999,
                    padding: '1px 7px',
                    minWidth: 18,
                    textAlign: 'center',
                  }}
                >
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>
      ) : null}

      <div
        style={{
          padding: '10px 16px',
          borderBottom: `1px solid ${border}`,
          background: bg,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 10,
          alignItems: 'flex-end',
        }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: '1 1 220px' }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>
            Buscar
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Search size={16} color={muted} aria-hidden />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Folio, RUT, razón social, tipo…"
              style={{ ...selectStyle, flex: 1, minWidth: 0, cursor: 'text' }}
            />
          </span>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 150 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>
            Estado
          </span>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={selectStyle}>
            <option value="">Todos</option>
            {statusOptions.map((st) => (
              <option key={st} value={st}>
                {st}
              </option>
            ))}
          </select>
        </label>
        {!showDocTypeTabs ? (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 180 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>
              Tipo doc.
            </span>
            <select value={filterType} onChange={(e) => setFilterType(e.target.value)} style={selectStyle}>
              <option value="">Todos</option>
              {typeOptions.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {hasActiveFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            style={{
              height: 36,
              padding: '0 12px',
              borderRadius: 8,
              border: `1px solid ${inputBorder}`,
              background: inputBg,
              color: text,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            Limpiar
          </button>
        ) : null}
      </div>

      {showBulkChrome && selectedIds.length > 0 ? (
        <div
          style={{
            padding: '10px 16px',
            borderBottom: `1px solid ${border}`,
            background: isDarkMode ? '#1c1917' : '#fafaf9',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 10,
            alignItems: 'center',
          }}
        >
          <span style={{ fontSize: 13, color: text, fontWeight: 600 }}>
            {selectedIds.length} seleccionado{selectedIds.length === 1 ? '' : 's'}
          </span>
          <button
            type="button"
            onClick={handleBulkDelete}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              height: 32,
              padding: '0 12px',
              borderRadius: 6,
              border: 'none',
              background: '#dc2626',
              color: '#fff',
              fontWeight: 600,
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            <Trash2 size={14} /> Anular
          </button>
        </div>
      ) : null}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 920 }}>
          <thead>
            <tr style={{ background: headerBg, borderBottom: `1px solid ${border}` }}>
              {showBulkChrome ? (
                <th style={{ padding: `${thPadV}px ${padH}px`, width: 36, textAlign: 'center' }}>
                  <input
                    ref={headerSelectRef}
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAllVisible}
                    aria-label="Seleccionar todos"
                  />
                </th>
              ) : null}
              {tableColumns.map((col) => (
                <th
                  key={col.key || 'actions'}
                  style={{
                    padding: `${thPadV}px ${padH}px`,
                    textAlign: col.align,
                    fontSize: 11,
                    fontWeight: 700,
                    color: muted,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                    cursor: col.sortKey ? 'pointer' : 'default',
                    userSelect: 'none',
                  }}
                  onClick={() => handleSortClick(col.sortKey)}
                >
                  {col.key ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      {col.key}
                      <SortIcon columnKey={col.sortKey} />
                    </span>
                  ) : null}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={colCount} style={{ padding: 24, textAlign: 'center', color: muted, fontSize: 13 }}>
                  Cargando registros…
                </td>
              </tr>
            ) : sortedRows.length === 0 ? (
              <tr>
                <td colSpan={colCount} style={{ padding: 24, textAlign: 'center', color: muted, fontSize: 13 }}>
                  {error || 'Sin registros aún.'}
                </td>
              </tr>
            ) : (
              sortedRows.map((row) => {
                const selected = selectedIds.includes(row.id);
                const displayStatus = rowDisplayStatus(row, showSaleLinkColumn);
                const statusTone = statusPresentation(displayStatus, isDarkMode);
                const dueHint = showSaleLinkColumn ? collectionDueHint(row) : '';
                return (
                  <tr
                    key={row.id}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = rowHover || headerBg;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = selected
                        ? `color-mix(in srgb, ${ui.interactive} 8%, ${bg})`
                        : bg;
                    }}
                    style={{
                      borderBottom: `1px solid ${border}`,
                      background: selected
                        ? `color-mix(in srgb, ${ui.interactive} 8%, ${bg})`
                        : bg,
                      cursor: onRowClick || onEdit ? 'pointer' : 'default',
                    }}
                    onClick={() => (onRowClick || onEdit)?.(row)}
                  >
                    {showBulkChrome ? (
                      <td
                        style={{ padding: `${padV}px ${padH}px`, textAlign: 'center' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleRow(row.id)}
                          aria-label={`Seleccionar ${row.doc_number || row.doc_code}`}
                        />
                      </td>
                    ) : null}
                    <td style={{ padding: `${padV}px ${padH}px` }}>
                      <Stacked
                        top={row.doc_number || row.doc_code || '—'}
                        bottom={showSaleLinkColumn ? (row.doc_date || row.doc_code || '') : [
                          row.doc_date || row.doc_code,
                          row.sale_code ? `Venta ${row.sale_code}` : '',
                        ].filter(Boolean).join(' · ')}
                        topColor={text}
                        muted={muted}
                      />
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, fontSize: 12, color: text }}>
                      {row.document_type || '—'}
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px` }}>
                      <Stacked
                        top={row.counterpart_name || '—'}
                        bottom={row.counterpart_rut || ''}
                        topColor={text}
                        muted={muted}
                      />
                    </td>
                    {showSaleLinkColumn ? (
                      <td style={{ padding: `${padV}px ${padH}px`, minWidth: 140 }}>
                        {String(row.sale_code || '').trim() ? (
                          <Stacked
                            top={row.sale_code}
                            bottom={saleLinkMethodLabel(row.link_method)}
                            topColor="#2563eb"
                            muted={muted}
                          />
                        ) : row.has_pending_sale_match && row.suggested_sale_code ? (
                          <div style={{ display: 'grid', gap: 6 }}>
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#b45309' }}>
                                Match · {row.suggested_sale_code}
                              </div>
                              <div style={{ fontSize: 11, color: muted, marginTop: 1 }}>
                                {Number(row.suggested_match_score || 0) > 0
                                  ? `Coincidencia ${Math.round(Number(row.suggested_match_score))}%`
                                  : 'Venta comercial pendiente'}
                              </div>
                            </div>
                            {onLinkSuggestedSale ? (
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  onLinkSuggestedSale(row);
                                }}
                                disabled={linkingSuggestedSale}
                                style={{
                                  height: 28,
                                  borderRadius: 8,
                                  border: 'none',
                                  background: ui.interactive,
                                  color: ui.textOnAccent || '#fff',
                                  padding: '0 8px',
                                  cursor: linkingSuggestedSale ? 'not-allowed' : 'pointer',
                                  fontSize: 11,
                                  fontWeight: 600,
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 4,
                                  width: 'fit-content',
                                }}
                              >
                                <Link2 size={12} />
                                Vincular venta
                              </button>
                            ) : null}
                          </div>
                        ) : (
                          <span style={{ fontSize: 12, color: muted, fontStyle: 'italic' }}>
                            Sin vincular
                          </span>
                        )}
                      </td>
                    ) : null}
                    <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'right', fontSize: 12, color: text }}>
                      {money(row.net_amount)}
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'right', fontSize: 12, color: text }}>
                      {money(row.tax_amount)}
                    </td>
                    <td
                      style={{
                        padding: `${padV}px ${padH}px`,
                        textAlign: 'right',
                        fontSize: 12,
                        fontWeight: 700,
                        color: text,
                      }}
                    >
                      {money(row.total_amount)}
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'center' }}>
                      <div style={{ display: 'grid', gap: 3, justifyItems: 'center' }}>
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '3px 9px',
                            borderRadius: 999,
                            fontSize: 11,
                            fontWeight: 650,
                            color: statusTone.color,
                            background: statusTone.bg,
                            border: `1px solid ${statusTone.border}`,
                          }}
                          title={
                            [
                              row.payment_terms ? `Pago: ${row.payment_terms}` : '',
                              dueHint,
                            ]
                              .filter(Boolean)
                              .join(' · ') || undefined
                          }
                        >
                          {statusTone.label}
                        </span>
                        {dueHint ? (
                          <span style={{ fontSize: 10, color: muted, whiteSpace: 'nowrap' }}>{dueHint}</span>
                        ) : null}
                      </div>
                    </td>
                    <td
                      style={{ padding: `${padV}px ${padH}px` }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                        {onEdit ? (
                          <button
                            type="button"
                            onClick={() => onEdit(row)}
                            style={{
                              height: 28,
                              width: 28,
                              borderRadius: 8,
                              border: `1px solid ${border}`,
                              background: 'transparent',
                              color: text,
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                            aria-label="Editar"
                          >
                            <Pencil size={13} />
                          </button>
                        ) : null}
                        {onDelete ? (
                          <button
                            type="button"
                            onClick={() => {
                              if (!window.confirm(`¿Anular ${row.doc_code || row.doc_number || 'registro'}?`)) return;
                              onDelete(row);
                            }}
                            style={{
                              height: 28,
                              width: 28,
                              borderRadius: 8,
                              border: `1px solid ${ui.danger || '#dc2626'}`,
                              background: 'transparent',
                              color: ui.danger || '#dc2626',
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                            aria-label="Anular"
                          >
                            <Trash2 size={13} />
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AccountingRegisterTable3;
